/**
 * ProjectDetailPage - Full project detail view (Orchestrator)
 *
 * The command center for working on a project.
 * This is the main container that orchestrates all child components.
 *
 * Components:
 * - HeaderBar: Condensed header with info and actions
 * - OperationQuickStartBar: Operation buttons with SKU selection
 * - CombinedSKUTable: SKU table with inline progress
 * - WorkOrderPanel: Right panel with iframe and notes
 * - SKUNoteModal: Modal for SKU-specific notes
 *
 * Theme: Light (bg-slate-200 base)
 * Reference: docs/architecture/2026-01-07/PROJECT-DETAIL-PAGE-REDESIGN.md
 */

import { useState, useEffect, useCallback } from "react";
import ReworkModal from "../ReworkModal";
import ProjectReworkSection from "../ProjectReworkSection";
import HeaderBar from "./HeaderBar";
import CombinedSKUTable from "./CombinedSKUTable";
import MilestoneGrid from "./MilestoneGrid";
import WorkOrderPanel from "./WorkOrderPanel";
import SKUNoteModal from "./SKUNoteModal";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function ProjectDetailPage({
  workOrder: workOrderProp,
  token,
  employeeId,
  employeeName,
  onBack: onBackProp,
  onStartTimer,
  onSubmitRework,
}) {
  // Get workOrder from prop or sessionStorage (for new nav pattern)
  const [workOrder, setWorkOrder] = useState(() => {
    if (workOrderProp) return workOrderProp;
    // Try to read from sessionStorage (set by PA View / Assembly View navigation)
    const stored = sessionStorage.getItem('3dcolor-selected-project');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing stored project:', e);
      }
    }
    return null;
  });

  // Default back handler - navigate back via history or to PA View
  const onBack = onBackProp || (() => {
    // Clear the stored project
    sessionStorage.removeItem('3dcolor-selected-project');
    // Navigate back - try history first, fallback to PA View
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.hash = '#/production/pa-view';
    }
  });

  // Core state
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [sendingNote, setSendingNote] = useState(false);

  // Modal state
  const [showRework, setShowRework] = useState(false);

  // Operation state
  const [projectOperations, setProjectOperations] = useState([]);
  const [loadingOps, setLoadingOps] = useState(false);

  // SKU Notes state
  const [skuNoteModal, setSkuNoteModal] = useState({ open: false, sku: null });
  const [skuNoteText, setSkuNoteText] = useState('');
  const [sendToPM, setSendToPM] = useState(false);
  const [savingSkuNote, setSavingSkuNote] = useState(false);
  const [skuNotes, setSkuNotes] = useState({});

  // ========== Data Fetching ==========

  const fetchProjectData = useCallback(async () => {
    const quoteNumber = workOrder?.quoteNumber || workOrder?.quote_number || workOrder?.id;
    if (!quoteNumber) return;
    setLoading(true);
    try {
      // Fetch from unified JobBoss + SharePoint endpoint
      const response = await fetch(
        `${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        // Merge with incoming workOrder data (which may have additional UI state)
        setProjectData({
          ...workOrder,
          ...data,
          // Normalize field names for compatibility
          project_name: data.projectName,
          wo_number: data.orderNumber || `Q-${data.quoteNumber}`,
          assigned_pm_name: data.pm,
          assigned_artist_name: data.artist,
          sales_rep: data.salesRep,
          due_date: data.shipDate,
          pa_status: data.paStatus,
          is_rush: data.isRush,
          has_opti: false, // Will be derived from routing
          special_instructions: data.specialInstructions,
          production_notes: data.notes,
          work_order_url: data.workOrderUrl,
          // Map jobs to skus for component compatibility
          skus: data.jobs || data.skus || [],
          sku_count: data.jobCount || data.jobs?.length || 0,
        });
      } else {
        // Fallback to workOrder prop
        setProjectData(workOrder);
      }
    } catch (err) {
      console.error("Error fetching project data:", err);
      setProjectData(workOrder);
    } finally {
      setLoading(false);
    }
  }, [workOrder?.quoteNumber, workOrder?.quote_number, workOrder?.id, token]);

  const fetchNotes = useCallback(async () => {
    // Use quoteNumber (preferred) or id for API call
    // Assembly View has compound id like "2003000-ready_to_work", so always prefer quoteNumber
    const projectId = workOrder?.quoteNumber || workOrder?.quote_number || workOrder?.id;
    if (!projectId) return;
    try {
      const response = await fetch(
        `${API_BASE}/api/pa-queue/${projectId}/notes`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
    }
  }, [workOrder?.quoteNumber, workOrder?.quote_number, workOrder?.id, token]);

  const fetchProjectOperations = useCallback(async () => {
    const quoteNumber = workOrder?.quoteNumber || workOrder?.quote_number || workOrder?.id;
    const orderNumber = workOrder?.orderNumber || workOrder?.wo_number;

    if (!quoteNumber && !orderNumber) return;

    setLoadingOps(true);
    try {
      // Try unified routing endpoint first (uses quoteNumber)
      if (quoteNumber) {
        const routingResponse = await fetch(
          `${API_BASE}/api/v2/production/projects/${encodeURIComponent(quoteNumber)}/routing`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (routingResponse.ok) {
          const data = await routingResponse.json();
          if (data.routing && data.routing.length > 0) {
            setProjectOperations(data.routing.map(op => ({
              workCenter: op.workCenter,
              operationCode: op.operationCode,
              description: op.description,
              status: op.status,
              complete: op.complete,
            })));
            setLoadingOps(false);
            return;
          }
        }
      }

      // Fallback to legacy orders endpoint
      if (orderNumber) {
        const response = await fetch(
          `${API_BASE}/api/orders/${encodeURIComponent(orderNumber)}/operations`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          const allOps = [];
          if (data.operationsByWorkCenter) {
            Object.entries(data.operationsByWorkCenter).forEach(([wc, ops]) => {
              ops.forEach(opCode => {
                allOps.push({ workCenter: wc, operationCode: opCode });
              });
            });
          }
          setProjectOperations(allOps);
        }
      }
    } catch (err) {
      console.error("Error fetching project operations:", err);
    } finally {
      setLoadingOps(false);
    }
  }, [workOrder?.quoteNumber, workOrder?.quote_number, workOrder?.id, workOrder?.orderNumber, workOrder?.wo_number, token]);

  useEffect(() => {
    fetchProjectData();
    fetchNotes();
    fetchProjectOperations();
  }, [fetchProjectData, fetchNotes, fetchProjectOperations]);

  // ========== Handlers ==========

  const handleSendNote = async (noteType = 'general') => {
    if (!newNote.trim() || sendingNote) return;
    const projectId = workOrder?.quoteNumber || workOrder?.quote_number || workOrder?.id;
    if (!projectId) return;
    setSendingNote(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/pa-queue/${projectId}/notes`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            content: newNote.trim(),
            noteType,
            mentionPM: noteType === 'question'
          }),
        }
      );
      if (response.ok) {
        setNewNote("");
        fetchNotes();
      }
    } catch (err) {
      console.error("Error sending note:", err);
    } finally {
      setSendingNote(false);
    }
  };

  const handleStartOperation = ({ operation, skuScope, selectedSkuIds }) => {
    const woWithContext = {
      ...projectData,
      selectedOperation: operation,
      selectedSkuScope: skuScope,
      selectedSkuIds,
    };
    if (onStartTimer) {
      onStartTimer(woWithContext);
    }
  };

  const openSkuNoteModal = (sku) => {
    const projectName = projectData?.project_name || projectData?.projectName || 'Unknown Project';
    setSkuNoteModal({ open: true, sku });
    setSkuNoteText(`Project: ${projectName}\nSKU: ${sku.sku}\nIssue: `);
    setSendToPM(false);
  };

  const handleSaveSkuNote = async () => {
    if (!skuNoteText.trim() || savingSkuNote) return;
    setSavingSkuNote(true);

    const { sku } = skuNoteModal;
    const newSkuNote = {
      id: Date.now(),
      content: skuNoteText.trim(),
      author_name: employeeName || 'Unknown',
      created_at: new Date().toISOString(),
      sent_to_pm: sendToPM,
    };

    setSkuNotes(prev => ({
      ...prev,
      [sku.sku]: [...(prev[sku.sku] || []), newSkuNote],
    }));

    if (sendToPM) {
      const projectId = workOrder?.quoteNumber || workOrder?.quote_number || workOrder?.id;
      if (!projectId) return;
      try {
        await fetch(
          `${API_BASE}/api/pa-queue/${projectId}/notes`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              content: `[SKU: ${sku.sku}] ${skuNoteText.trim()}`,
              noteType: 'question',
              mentionPM: true
            }),
          }
        );
        fetchNotes();
      } catch (err) {
        console.error("Error sending SKU note to PM:", err);
      }
    }

    setSkuNoteModal({ open: false, sku: null });
    setSkuNoteText('');
    setSendToPM(false);
    setSavingSkuNote(false);
  };

  // ========== Render ==========

  // No project selected - show message and back button
  if (!workOrder) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex flex-col items-center justify-center gap-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Project Selected</h2>
          <p className="text-gray-500">Select a project from the PA View or Assembly View to see details.</p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
        >
          Go to PA View
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 bg-slate-200 text-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  const wo = projectData || workOrder;

  const hasOptiOperations = wo.routing?.some(op =>
    op.operationNumber?.toString().includes('OPTI') ||
    op.description?.toLowerCase().includes('opti')
  ) || wo.has_opti || wo.hasOpti;

  const workOrderUrl = wo.work_order_url || wo.workOrderUrl;

  return (
    <div className="h-screen flex flex-col bg-slate-200 text-gray-900">
      {/* Header */}
      <HeaderBar
        workOrder={wo}
        hasOptiOperations={hasOptiOperations}
        onBack={onBack}
        onStartTimer={onStartTimer}
        onReworkClick={() => setShowRework(true)}
      />

      {/* Rework Modal */}
      <ReworkModal
        isOpen={showRework}
        onClose={() => setShowRework(false)}
        employeeId={employeeId}
        displayName={employeeName}
        token={token}
      />

      {/* SKU Note Modal */}
      <SKUNoteModal
        isOpen={skuNoteModal.open}
        sku={skuNoteModal.sku}
        noteText={skuNoteText}
        onNoteTextChange={setSkuNoteText}
        sendToPM={sendToPM}
        onSendToPMChange={setSendToPM}
        existingNotes={skuNoteModal.sku ? skuNotes[skuNoteModal.sku.sku] : []}
        onSave={handleSaveSkuNote}
        onClose={() => {
          setSkuNoteModal({ open: false, sku: null });
          setSkuNoteText('');
          setSendToPM(false);
        }}
        saving={savingSkuNote}
      />

      {/* SCROLL INVARIANT: Do not remove min-h-0; required for contained scrolling.
          Only two scroll containers allowed: SKU panel and Work Order panel.
          Never add nested scrolling inside rows or modals. */}
      <div className="flex flex-1 min-h-0">
        {/* Left Panel - Milestone Grid + Rework Section */}
        <div className="flex flex-col flex-1 min-h-0 overflow-auto">
          {/* Milestone Progress Grid */}
          <div className="p-4">
            <MilestoneGrid
              quoteNumber={wo.quoteNumber || wo.quote_number || wo.id}
              jobs={wo.skus || wo.jobs || []}
              token={token}
            />
          </div>

          {/* Project Rework Section - v2.1 */}
          <div className="p-4 bg-slate-100 border-t border-gray-200">
            <ProjectReworkSection
              quoteNumber={wo.quoteNumber || wo.quote_number || wo.id}
              projectName={wo.projectName || wo.project_name}
              context={{
                orderNumber: wo.orderNumber || wo.order_number,
                workCenter: 'PA',
              }}
            />
          </div>
        </div>

        {/* Right Panel - Work Order & Notes */}
        <WorkOrderPanel
          workOrder={wo}
          workOrderUrl={workOrderUrl}
          notes={notes}
          newNote={newNote}
          onNewNoteChange={setNewNote}
          onSendNote={handleSendNote}
          sendingNote={sendingNote}
        />
      </div>
    </div>
  );
}
